import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "../../../components/feature/SiteHeader";
import SiteFooter from "../../../components/feature/SiteFooter";
import usePageTitle from "../../../hooks/usePageTitle";
import { articles } from "../../../mocks/articles";

export default function AnalysisDetail() {
  const { id } = useParams();
  const article = articles.find((a) => a.id === id) ?? articles[0];

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  usePageTitle(article?.title);

  if (!article) {
    return null;
  }

  const related = articles.filter((a) => a.id !== article.id).slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    articleSection: article.category,
    dateModified: article.updatedAt,
    inLanguage: "zh-CN",
    author: { "@type": "Organization", name: "甜心剧情站" },
    image: article.cover,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <SiteHeader />
      <main>
        <section className="bg-gradient-to-b from-primary-100/70 to-background-50 pt-24 md:pt-28 pb-8">
          <div className="mx-auto max-w-3xl px-4 md:px-6">
            <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-600" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-400" />
              <Link to="/analysis" className="hover:text-primary-600">剧情解析</Link>
              <i className="ri-arrow-right-s-line text-foreground-400" />
              <span className="text-foreground-800">{article.category}</span>
            </nav>
            <span className="mt-6 inline-flex rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              {article.category}
            </span>
            <h1 className="mt-3 font-display text-2xl leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="inline-flex items-center gap-1.5"><i className="ri-time-line" />{article.readTime}</span>
              <span className="inline-flex items-center gap-1.5"><i className="ri-calendar-line" />更新于 {article.updatedAt}</span>
              <span className="inline-flex items-center gap-1.5"><i className="ri-user-heart-line" />甜心剧情站</span>
            </div>
          </div>
        </section>

        <article className="mx-auto max-w-3xl px-4 md:px-6 py-8">
          <img
            src={article.cover}
            alt={article.title}
            className="aspect-[16/9] w-full rounded-2xl object-cover object-top"
          />
          <p className="mt-6 text-base leading-8 text-foreground-800">{article.summary}</p>

          {article.content.map((sec) => (
            <section key={sec.heading} className="mt-8">
              <h2 className="font-display text-xl text-foreground-950 md:text-2xl">{sec.heading}</h2>
              {sec.body.map((p, i) => (
                <p key={i} className="mt-4 text-base leading-8 text-foreground-700">
                  {p}
                </p>
              ))}
            </section>
          ))}

          <div className="mt-8 flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-background-100 px-3 py-1 text-sm text-foreground-600">
                #{tag}
              </span>
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-3 rounded-2xl border border-background-200/70 bg-background-100 p-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-700">
              想从头了解整部剧情？分集速览都在这里。
            </p>
            <Link
              to="/episodes"
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm text-background-50 whitespace-nowrap hover:bg-primary-600"
            >
              查看分集剧情 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </article>

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-10">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-display text-2xl text-foreground-950">相关阅读</h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
              {related.map((a) => (
                <Link
                  key={a.id}
                  to={`/analysis/${a.id}`}
                  className="group overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 transition-colors hover:border-primary-200"
                >
                  <div className="h-28 overflow-hidden">
                    <img
                      src={a.cover}
                      alt={a.title}
                      className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-primary-600">{a.category}</span>
                    <h3 className="mt-1 font-medium text-sm leading-snug text-foreground-950 group-hover:text-primary-700">
                      {a.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}